import React, { useCallback } from "react";

import Interweave from "interweave";
import { keyBy } from "lodash";
import { serializeError } from "serialize-error";

import { type ConnectionAccountMetadata } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { isAxiosError } from "@utils/index";

import {
  type Account,
  AccountSelectionList,
} from "./add-additional-sub-account";

export interface AccountMetadataWithDescription
  extends ConnectionAccountMetadata {
  descriptionHTML?: string;
}

export interface AccountSelectorProps {
  tag: string;
  accountList: AccountMetadataWithDescription[];

  isSaving: boolean;
  onSave: (selectedId: ConnectionAccountMetadata) => Promise<any>;
  onConnectAnotherAccount?(): void;
}

export default function AccountSelector({
  tag,
  accountList,
  isSaving,
  onSave,
  onConnectAnotherAccount,
}: AccountSelectorProps) {
  const [selected, setSelected] =
    React.useState<AccountMetadataWithDescription>();
  const [error, setError] = React.useState<string>("");

  const accountsById = React.useMemo(() => {
    return keyBy(accountList, "id");
  }, [accountList]);
  const isMounted = React.useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const save = React.useCallback(async () => {
    if (!selected) {
      setError("Please select an account to link.");
      return;
    }

    try {
      await onSave(selected);
      setError("");
    } catch (e) {
      if (!isMounted.current) {
        return;
      }

      if (isAxiosError(e)) {
        setError(e.response?.data?.toString() || "Unknown error");
      } else {
        setError(JSON.stringify(serializeError(e)));
      }
    }
  }, [selected, onSave]);

  const hasAccountsToBeConnected = accountList.length > 0;

  const SubtextRow = useCallback(
    ({ account }: { account: Account }) => {
      const accountMetadata = accountsById[account.accountId];

      return (
        accountMetadata.descriptionHTML && (
          <>
            {" "}
            &emsp;(
            <Interweave content={accountMetadata.descriptionHTML} />)
          </>
        )
      );
    },

    [accountsById],
  );

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          <b>An error occurred!</b> {error}
        </div>
      )}
      <div className="mb-3">
        {!hasAccountsToBeConnected && (
          <p className="alert alert-danger flex flex-col" role="alert">
            You have no active accounts to connect.
            {onConnectAnotherAccount && (
              <u className="cursor-pointer" onClick={onConnectAnotherAccount}>
                Connect with another account
              </u>
            )}
          </p>
        )}
        {hasAccountsToBeConnected && (
          <>
            <p>Select an account to connect:</p>
            <AccountSelectionList
              accounts={accountList.map((adAccount) => ({
                accountId: adAccount.id,
                accountName: adAccount.name,
              }))}
              selectedAccount={
                selected
                  ? {
                      accountId: selected.id,
                      accountName: selected.name,
                    }
                  : undefined
              }
              setSelectedAccount={(account) => {
                setSelected(accountsById[account.accountId]);
              }}
              renderSubtext={SubtextRow}
            />
            <PrimaryButton
              onClick={save}
              loading={isSaving}
              isDisabled={isSaving}
            >
              Save
            </PrimaryButton>
          </>
        )}
      </div>
    </>
  );
}
